/**
 * What media-api's connector endpoints must send for the connectors surface to render them.
 *
 * `looseObject` throughout, and closed unions read through {@link wireUnion}: see
 * `entities/media-campaign/api/schemas.ts` for why both hold across this surface.
 */
import { z } from 'zod';
import { wireUnion } from '@/lib/api/parse-response';
import type {
  ConnectorCapabilities,
  ListConnectorsResponse,
  ListPlatformsResponse,
  ListSyncJobStatusResponse,
  MediaConnector,
} from '../model/types';

const capabilitySchema = z.looseObject({
  state: wireUnion<ConnectorCapabilities['signIn']['state']>(),
  checkedAt: z.string().nullable(),
  error: z.string().nullable(),
});

const capabilitiesSchema: z.ZodType<ConnectorCapabilities> = z.looseObject({
  signIn: capabilitySchema,
  publishing: capabilitySchema,
  reporting: capabilitySchema,
});

export const mediaConnectorSchema: z.ZodType<MediaConnector> = z.looseObject({
  id: z.string(),
  workspaceId: z.string(),
  platform: wireUnion<MediaConnector['platform']>(),
  status: wireUnion<MediaConnector['status']>(),
  authMethod: wireUnion<MediaConnector['authMethod']>(),
  title: z.string(),
  tags: z.array(z.string()),
  syncCadence: wireUnion<MediaConnector['syncCadence']>(),
  externalAccountId: z.string().nullable().optional(),
  externalAccountName: z.string().nullable().optional(),
  dsaBeneficiary: z.string().nullable().optional(),
  dsaPayor: z.string().nullable().optional(),
  advertiserId: z.string().nullable().optional(),
  advertiserName: z.string().nullable().optional(),
  tokenExpiresAt: z.string().nullable().optional(),
  connectedAt: z.string().nullable().optional(),
  connectedBy: z.string(),
  errorMessage: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  capabilities: capabilitiesSchema.optional(),
});

export const connectorListSchema: z.ZodType<ListConnectorsResponse> = z.looseObject({
  items: z.array(mediaConnectorSchema),
});

export const platformListSchema: z.ZodType<ListPlatformsResponse> = z.looseObject({
  items: z.array(
    z.looseObject({
      platform: wireUnion<MediaConnector['platform']>(),
      label: z.string(),
      description: z.string(),
      category: wireUnion<ListPlatformsResponse['items'][number]['category']>(),
      authMethods: z.array(wireUnion<MediaConnector['authMethod']>()),
      connectedAccountCount: z.number(),
      supportsBrowsing: z.boolean(),
    }),
  ),
});

export const syncStatusListSchema: z.ZodType<ListSyncJobStatusResponse> = z.looseObject({
  items: z.array(
    z.looseObject({
      id: z.string(),
      connectorId: z.string(),
      status: wireUnion<ListSyncJobStatusResponse['items'][number]['status']>(),
      lastSyncAt: z.string().nullable(),
      syncWindowStart: z.string().nullable(),
      syncWindowEnd: z.string().nullable(),
      lastError: z.string().nullable(),
      entriesSynced: z.number(),
    }),
  ),
});

/**
 * The redirect an OAuth connect starts at. Worth a schema of its own: an absent `authUrl`
 * used to send the popup to the string "undefined", which reads as the platform refusing us.
 */
export const oauthInitiateSchema = z.looseObject({ authUrl: z.string() });
